import { useState, useEffect, useRef } from 'react';
import {
	IconNavigationChevronArrowUpComponent,
	IconNavigationChevronArrowDownComponent,
	IconActionPlusComponent
} from '@vaa-component-lib/component.atom.icons';
import {
	ButtonColour,
	ButtonComponent,
	ButtonSize,
	ButtonType
} from '@vaa-component-lib/component.atom.button';

import PopOverComponent, {
	PopOverType,
	PopOverAlignment
} from '../pop-over/pop-over.component';
import sanitiseString from '../../lib/utils/sanitise-string';
import { useFormContext } from 'react-hook-form';
import { TextFieldComponent } from '@vaa-component-lib/component.atom.text-field';
import {
	IconNavigationChevronDownComponent,
	IconNavigationChevronUpComponent,
	IconsSize
} from '@vaa-component-lib/component.atom.icons';
import {
	DiscountCodeContainer,
	DiscountCodeWrapper,
	DiscountButton,
	DiscountButtonText
} from './discount-code.styles';
import useMediaQuery, { BreakPointToken } from '../../hooks/useMediaQuery';

interface DiscountCodeComponentProps {
	label: string;
	value?: string;
	name: string;
}

const DiscountCodeComponent = ({
	label,
	name,
	value
}: DiscountCodeComponentProps) => {
	const { setValue } = useFormContext();
	const [code, setCode] = useState<string>(value || '');
	const [inputValue, setInputValue] = useState<string>(value || '');
	const [isOpened, setIsOpened] = useState<boolean>(false);
	const ref = useRef<HTMLDivElement>(null);
	const isTabletLayout = useMediaQuery(
		`(min-width: ${BreakPointToken.SmMin}px) and (max-width: ${BreakPointToken.MdMax}px)`
	);

	const handleClick = (e: MouseEvent) => {
		e.preventDefault();
		setIsOpened(!isOpened);
	};

	const handleClear = () => {
		setInputValue('');
		setCode('');
		setValue(name, null, {
			shouldValidate: true,
			shouldDirty: true
		});
	};

	useEffect(() => {
		setValue(name, code, {
			shouldValidate: true,
			shouldDirty: true
		});
	}, [code]);

	const handleClickOutside = (event: MouseEvent) => {
		if (ref.current && !ref.current.contains(event.target as Node)) {
			setIsOpened(false);
		}
	};

	useEffect(() => {
		if (document) {
			if (isOpened) {
				document.addEventListener('mousedown', handleClickOutside);
			}

			return () =>
				document.removeEventListener('mousedown', handleClickOutside);
		}
	}, [isOpened]);

	const popOverId = `popover-${sanitiseString(label)}`;

	return (
		<DiscountCodeWrapper ref={ref} data-cy="discount-code-component">
			<DiscountButton
				onClick={(e: any): void => handleClick(e)}
				type="button"
				aria-controls={popOverId}
				aria-haspopup="true"
				open={isOpened}
				data-testid="discount-button">
				<DiscountButtonText>
					{code?.length > 0 ? `code: ${code}` : label}
				</DiscountButtonText>
				{isOpened ? (
					<IconNavigationChevronUpComponent size={IconsSize.Xsm} />
				) : (
					<IconNavigationChevronDownComponent size={IconsSize.Xsm} />
				)}
			</DiscountButton>
			<PopOverComponent
				isVisible={isOpened}
				type={PopOverType.Selector}
				alignment={
					isTabletLayout ? PopOverAlignment.Left : PopOverAlignment.Right
				}
				title={'Discount code'}
				onClose={(): void => setIsOpened(false)}
				id={popOverId}>
				<DiscountCodeContainer>
					<TextFieldComponent
						label={label}
						autoComplete="off"
						spellCheck={false}
						value={inputValue}
						onChange={(e) => setInputValue(e.target.value)}
						clearable={true}
						onClear={handleClear}
						aria-controls={popOverId}
						aria-haspopup="true"
						// isValid={code}
						tabIndex={isOpened ? 0 : -1}
					/>
					<ButtonComponent
						colour={ButtonColour.Brand}
						disabled={inputValue.length < 1}
						size={ButtonSize.Medium}
						iconChild={<IconActionPlusComponent />}
						type={ButtonType.Button}
						onClick={(e) => setCode(inputValue)}
						data-testid="discount-code-submit-button"
						// @ts-ignore
						tabIndex={isOpened ? 0 : -1}
					/>
				</DiscountCodeContainer>
			</PopOverComponent>
		</DiscountCodeWrapper>
	);
};

export default DiscountCodeComponent;
